import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/20/solid";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { StepDisplayState } from "../../../api/threads/liveMessages";
import { appPaths } from "../../../shared/routing/appPaths";
import { WorkspaceLink } from "../../../shell/workspace";
import { classNames } from "../../../ui/styles/classNames";
import type { ItemAtVersion } from "../../threads/prepare";
import { STEP_STATE_CHIP } from "../../threads/status/stepStateTone";
import { stepStateText } from "../../threads/status/stepStateWords";
import EditItemButton from "../inputCards/EditItemButton";
import { isExpandable, type GraphNode } from "../model/workflowGraph";
import { useGraphActions } from "./graphActions";
import { nodeAddress } from "./nodeAddress";

/**
 * A node's title, as the link to what it names. `stretched` makes the whole card the link:
 * the link's box covers the card, and the card's other controls sit above it.
 */
export function NodeTitle({ node, title, stretched = false, className }: { node: GraphNode; title: string; stretched?: boolean; className?: string }) {
  const address = nodeAddress(node);
  const titleClass = classNames("app-title font-semibold", className);
  if (address === null) {
    return <span className={titleClass} title={title}>{title}</span>;
  }
  return (
    <Link
      to={address}
      title={title}
      className={classNames(
        titleClass,
        "underline-offset-2 hover:underline",
        stretched ? "after:absolute after:inset-0 after:rounded-[inherit] focus-visible:outline-none focus-visible:after:[outline:2px_solid_var(--app-focus-ring)]" : "",
      )}
    >
      {title}
    </Link>
  );
}

/**
 * A node's other controls, the same in the graph and in the list: open or close it in
 * place, and the links to where it is used or was made. Each control names the node it
 * acts on, because a screen reader reaches it without the node's card around it. They sit
 * above a stretched title link.
 */
export function NodeControls({ node, title }: { node: GraphNode; title: string }) {
  const { t } = useTranslation();
  const { toggle, stepLinkInThread } = useGraphActions();
  const linkClass = "app-link text-xs underline";
  const threadLink = node.kind === "step" && node.progressKey !== null && stepLinkInThread !== null ? stepLinkInThread(node.progressKey) : null;
  const hasControls = isExpandable(node) || threadLink !== null || (node.kind === "artifact" && producerOf(node) !== null);
  if (!hasControls) {
    return null;
  }
  return (
    <span className="nodrag nopan relative z-10 flex shrink-0 flex-wrap items-center gap-x-2 gap-y-0.5">
      {isExpandable(node) ? (
        <button
          type="button"
          className="app-link text-xs font-semibold"
          aria-expanded={node.expanded}
          aria-label={t(node.expanded ? "workflows.node.collapseNamed" : "workflows.node.expandNamed", { name: title })}
          onClick={() => {
            toggle(node.id);
          }}
        >
          {t(node.expanded ? "workflows.node.collapse" : "workflows.node.expand")}
        </button>
      ) : null}
      {threadLink === null ? null : (
        <WorkspaceLink route={threadLink} placement="beside" className={linkClass} ariaLabel={t("workflows.node.showInThreadNamed", { name: title })}>
          {t("workflows.node.showInThread")}
        </WorkspaceLink>
      )}
      {node.kind === "artifact" ? <ArtifactLinks node={node} title={title} className={linkClass} /> : null}
    </span>
  );
}

function ArtifactLinks({ node, title, className }: { node: Extract<GraphNode, { kind: "artifact" }>; title: string; className: string }) {
  const { t } = useTranslation();
  const producer = producerOf(node);
  const thread = node.artifact.madeBy?.thread ?? null;
  return (
    <>
      {producer === null ? null : (
        <Link to={appPaths.workflow(producer)} className={className} aria-label={t("workflows.node.openProducerNamed", { name: title })}>
          {t("workflows.node.openProducer")}
        </Link>
      )}
      {thread === null ? null : (
        <Link to={appPaths.thread(thread)} className={className} aria-label={t("workflows.node.openProducerThreadNamed", { name: title })}>
          {t("workflows.node.openProducerThread")}
        </Link>
      )}
    </>
  );
}

/** The Workflow that made the artifact, or that builds it; `null` if none is known. */
function producerOf(node: Extract<GraphNode, { kind: "artifact" }>): string | null {
  return node.producer?.reference ?? node.artifact.madeBy?.workflow ?? null;
}

/**
 * The chevron on a card: an input that holds steps opens in place to show them; any other
 * card opens to show its settings. Either way it says what it opens, for a screen reader.
 * `stretched` makes the whole closed card open when clicked, so viewing is the card's
 * standard action (F51); the card's other controls sit above it.
 */
export function CardChevron({ node, title, open, stretched = false }: { node: GraphNode; title: string; open: boolean; stretched?: boolean }) {
  const { t } = useTranslation();
  const { toggle, toggleCard } = useGraphActions();
  const frame = isExpandable(node);
  const expanded = frame ? node.expanded : open;
  const label = frame
    ? t(expanded ? "workflows.node.collapseNamed" : "workflows.node.expandNamed", { name: title })
    : t(expanded ? "workflows.node.hideSettingsNamed" : "workflows.node.showSettingsNamed", { name: title });
  const Icon = expanded ? ChevronDownIcon : ChevronRightIcon;
  return (
    <button
      type="button"
      className={classNames(
        "app-flow-chevron nodrag nopan shrink-0",
        stretched ? "after:absolute after:inset-0 after:rounded-[inherit] focus-visible:outline-none focus-visible:after:[outline:2px_solid_var(--app-focus-ring)]" : "relative z-10",
        expanded ? "app-flow-chevron-open" : "",
      )}
      aria-expanded={expanded}
      aria-label={label}
      title={label}
      data-opens={frame ? "frame" : "settings"}
      onClick={() => {
        if (frame) {
          toggle(node.id);
        } else {
          toggleCard(node.id);
        }
      }}
    >
      <Icon className="size-3.5" aria-hidden="true" />
    </button>
  );
}

/**
 * Edit for one card's item (F51): opens the edit Thread on it beside the graph, where the
 * agent or the manual way makes the change (F36, ADR-0031 D2). Never a modal.
 */
export function EditButton({ item, title }: { item: ItemAtVersion; title: string }) {
  const { edit } = useGraphActions();
  return edit === null ? null : <EditItemButton item={item} title={title} onEdit={edit} compact={true} />;
}

/** The links of an artifact: the Workflow that makes it, the Thread that made this version. */
export function ArtifactLinksOf({ node, title }: { node: Extract<GraphNode, { kind: "artifact" }>; title: string }) {
  return <ArtifactLinks node={node} title={title} className="app-link nodrag nopan relative z-10 text-xs underline" />;
}

/** A step's number in its Workflow, the same in the graph, the list and a Thread. */
export function StepNumber({ number }: { number: string }) {
  return <span className="app-flow-number shrink-0 font-mono text-xs font-semibold">{number}</span>;
}

/** Marks a step that runs only when someone starts it. */
export function OptionalChip({ text }: { text: string }) {
  return <span className="app-chip app-flow-optional-chip shrink-0 px-1.5 py-0 text-[11px]">{text}</span>;
}

/** A step's state in a Run; with `link`, it opens the Run view beside the graph there (WD-4). */
export function StepStateChip({ state, link = null, title = "" }: { state: StepDisplayState; link?: string | null; title?: string }) {
  const { t } = useTranslation();
  const className = classNames("app-chip shrink-0 px-1.5 py-0 text-[11px]", STEP_STATE_CHIP[state]);
  if (link === null) {
    return <span className={className}>{stepStateText(state, t)}</span>;
  }
  return (
    <WorkspaceLink route={link} placement="beside" className={classNames(className, "nodrag nopan relative z-10 underline-offset-2 hover:underline")} ariaLabel={t("workflows.node.showInThreadNamed", { name: title })}>
      {stepStateText(state, t)}
    </WorkspaceLink>
  );
}
